/**
 * Goal Store
 *
 * Zustand (vanilla) store for the user-goal feature. Goals are a single-source
 * owned resource with a full CRUD + status lifecycle, so this follows the
 * challenge/plan shape (the list lives in the store).
 *
 * The goal index returns the user's complete set in one response, so there is
 * no pagination state here — filtering and sorting happen against the list the
 * store already holds.
 *
 * The status transitions (cancel / resume / lock) live here rather than in the
 * page because each one invalidates the list the store owns: after a
 * transition the store refetches so every consumer sees the new status.
 *
 * Create/update re-throw after recording the error so a form page can keep the
 * user on the form. The list fetch records its error without re-throwing (the
 * list page renders it inline with a retry).
 */

import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { registerSessionScoped } from './session-scope';
import { userGoalService } from '../services/user-goal.service';
import { dayjs } from '../utils/date';

import type { FetchOptions } from './fetch-options';
import type { ID } from '../types';
import type {
  UserGoalQueryParams,
  UserGoals,
  UserGoalStatus,
} from '../types/domain/user-goal.types';
import type { UserGoalInput } from '../validation/user-goal.schemas';

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface GoalStoreState {
  // Loading states
  isFetching: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  /** Id of the goal currently undergoing a status transition, if any */
  transitioningId: ID | null;

  // Error states
  fetchError: string | null;
  createError: string | null;
  updateError: string | null;

  // Data state
  goals: UserGoals[];
  /** Username the current list belongs to, so transitions can refetch */
  username: string | null;
}

export interface GoalStoreActions {
  fetchGoals: (
    username: string,
    params?: UserGoalQueryParams,
    options?: FetchOptions
  ) => Promise<void>;
  createGoal: (username: string, data: UserGoalInput) => Promise<void>;
  updateGoal: (id: ID, data: UserGoalInput) => Promise<void>;
  deleteGoal: (id: ID) => Promise<boolean>;
  restoreGoal: (id: ID) => Promise<boolean>;

  cancelGoal: (id: ID) => Promise<void>;
  resumeGoal: (id: ID) => Promise<void>;
  lockGoal: (id: ID) => Promise<void>;

  clearErrors: () => void;
  reset: () => void;
}

export type GoalStore = GoalStoreState & GoalStoreActions;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

const INITIAL_STATE: GoalStoreState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  transitioningId: null,
  fetchError: null,
  createError: null,
  updateError: null,
  goals: [],
  username: null,
};

export const goalStore = createStore<GoalStore>((set, get) => ({
  ...INITIAL_STATE,

  // =========================================================================
  // CRUD ACTIONS
  // =========================================================================

  fetchGoals: async (
    username: string,
    params?: UserGoalQueryParams,
    { fresh = false }: FetchOptions = {}
  ): Promise<void> => {
    if (fresh) userGoalService.clearCache();
    set({ isFetching: true, fetchError: null, username });

    try {
      const goals = await userGoalService.getUserGoals(username, params);
      set({ goals });
    } catch (error) {
      set({ fetchError: failureMessage(error, 'Failed to fetch goals') });
    } finally {
      set({ isFetching: false });
    }
  },

  createGoal: async (username: string, data: UserGoalInput): Promise<void> => {
    set({ isCreating: true, createError: null });

    try {
      await userGoalService.createUserGoal(username, data);
    } catch (error) {
      set({ createError: failureMessage(error, 'Failed to create goal') });
      throw error;
    } finally {
      set({ isCreating: false });
    }
  },

  updateGoal: async (id: ID, data: UserGoalInput): Promise<void> => {
    set({ isUpdating: true, updateError: null });

    try {
      await userGoalService.updateUserGoal(id, data);
    } catch (error) {
      set({ updateError: failureMessage(error, 'Failed to update goal') });
      throw error;
    } finally {
      set({ isUpdating: false });
    }
  },

  deleteGoal: async (id: ID): Promise<boolean> => {
    try {
      await userGoalService.deleteUserGoal(id);
      // Drop it locally rather than refetching — the undo toast needs the row
      // gone immediately, and a restore re-syncs from the server anyway.
      set({ goals: get().goals.filter((goal) => goal.id !== id) });
      return true;
    } catch (error) {
      set({ updateError: failureMessage(error, 'Failed to delete goal') });
      return false;
    }
  },

  restoreGoal: async (id: ID): Promise<boolean> => {
    try {
      await userGoalService.restoreUserGoal(id);

      // Re-sync so the restored goal reappears with its canonical position
      // and server-derived status.
      const { username } = get();
      if (username) {
        const goals = await userGoalService.getUserGoals(username);
        set({ goals });
      }
      return true;
    } catch (error) {
      set({ updateError: failureMessage(error, 'Failed to restore goal') });
      return false;
    }
  },

  // =========================================================================
  // STATUS TRANSITIONS
  // =========================================================================

  cancelGoal: async (id: ID): Promise<void> => {
    await runTransition(set, get, id, () => userGoalService.cancelGoal(id), {
      status: 'Canceled',
    });
  },

  resumeGoal: async (id: ID): Promise<void> => {
    await runTransition(
      set,
      get,
      id,
      () => userGoalService.resumeGoal(id),
      (goal) => ({ status: resumedStatus(goal) })
    );
  },

  lockGoal: async (id: ID): Promise<void> => {
    await runTransition(set, get, id, () => userGoalService.lockGoal(id), {
      locked: true,
    });
  },

  // =========================================================================
  // STATE MANAGEMENT ACTIONS
  // =========================================================================

  clearErrors: (): void => {
    set({ fetchError: null, createError: null, updateError: null });
  },

  reset: (): void => {
    set({ ...INITIAL_STATE });
  },
}));

/**
 * Recomputes the status a resumed goal returns to.
 *
 * Resuming clears the `canceled` timestamp, and the server derives status from
 * there: with nothing completed or canceled it is `Ongoing` once the target
 * date has passed and `Active` while it is still ahead. Assuming `Active`
 * would mislabel an overdue goal the moment it is resumed.
 */
function resumedStatus(goal: UserGoals): UserGoalStatus {
  // The API serialises target_date at midnight UTC; read the calendar day only
  // so a viewer west of UTC does not see today's deadline as already past.
  return dayjs(goal.target_date.slice(0, 10)).isBefore(dayjs().startOf('day'))
    ? 'Ongoing'
    : 'Active';
}

/**
 * Runs a status transition and applies its result to the held list.
 *
 * The transition endpoints return no body, so there is nothing to merge from a
 * response: the store applies the same derivation the server would, to the one
 * goal that changed. `patch` is either a fixed set of fields or a function of
 * the current goal, for transitions whose result depends on it.
 *
 * The service drops the cached list on its own, so the patch is not undone by
 * the next `fetchGoals` inside the cache TTL.
 */
async function runTransition(
  set: (partial: Partial<GoalStore>) => void,
  get: () => GoalStore,
  id: ID,
  transition: () => Promise<void>,
  patch: Partial<UserGoals> | ((goal: UserGoals) => Partial<UserGoals>)
): Promise<void> {
  set({ transitioningId: id, updateError: null });

  try {
    await transition();

    set({
      goals: get().goals.map((goal) =>
        goal.id === id
          ? { ...goal, ...(typeof patch === 'function' ? patch(goal) : patch) }
          : goal
      ),
    });
  } catch (error) {
    set({ updateError: failureMessage(error, 'Failed to update goal') });
    throw error;
  } finally {
    set({ transitioningId: null });
  }
}

// =============================================================================
// SELECTORS
// =============================================================================

export const selectGoals = (state: GoalStore): UserGoals[] => state.goals;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectGoalLoading = (state: GoalStore) => ({
  isFetching: state.isFetching,
  isCreating: state.isCreating,
  isUpdating: state.isUpdating,
});

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectGoalErrors = (state: GoalStore) => ({
  fetchError: state.fetchError,
  createError: state.createError,
  updateError: state.updateError,
});

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('goalStore', () => {
  goalStore.getState().reset();
});
